import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckSquare, User, CalendarDays, Clock, CheckCircle, XCircle } from 'lucide-react';

export default function RegularizationApprovalsPage() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentMap, setCommentMap] = useState({});
  const [processing, setProcessing] = useState({});

  useEffect(() => { fetchPending(); }, []);

  async function fetchPending() {
    setLoading(true);
    try {
      const res = await api.get('/hr/regularization-requests?pending_approval=true');
      setPending(res.data || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(id, action) {
    setProcessing(p => ({ ...p, [id]: action }));
    try {
      await api.put(`/hr/regularization-requests/${id}/approve?action=${action}&comments=${encodeURIComponent(commentMap[id] || '')}`);
      toast.success(`Request ${action}d`);
      fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setProcessing(p => ({ ...p, [id]: null }));
    }
  }

  return (
    <div className="space-y-6" data-testid="regularization-approvals-page">
      <div>
        <h1 className="text-2xl font-bold">Regularization Approvals</h1>
        <p className="text-muted-foreground text-sm mt-1">Review and approve/reject attendance regularization requests</p>
      </div>

      <Badge variant="outline">{pending.length} pending approval(s)</Badge>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckSquare className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-1">All Clear</h3>
            <p className="text-muted-foreground text-sm">No pending regularization requests to review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map((req, i) => (
            <Card key={req.id || i} data-testid={`approval-card-${i}`}>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-yellow-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{req.employee_name || 'Employee'}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{req.date || 'N/A'}</span>
                      {req.type && <Badge variant="outline" className="text-xs">{req.type}</Badge>}
                      {req.department && <span>{req.department}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs mt-1">
                      {req.original_in && <span className="text-muted-foreground">Original: {req.original_in} - {req.original_out || '--'}</span>}
                      {req.corrected_in && <span className="text-primary font-medium">Requested: {req.corrected_in} - {req.corrected_out || '--'}</span>}
                    </div>
                    {req.reason && <p className="text-xs text-muted-foreground mt-1">{req.reason}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-14">
                  <Textarea
                    placeholder="Comments (optional)"
                    className="h-9 min-h-[36px] text-sm flex-1"
                    value={commentMap[req.id] || ''}
                    onChange={e => setCommentMap(m => ({ ...m, [req.id]: e.target.value }))}
                  />
                  <Button size="sm" variant="outline" className="text-green-600 border-green-600/30 hover:bg-green-600/10"
                    disabled={!!processing[req.id]} onClick={() => handleAction(req.id, 'approve')} data-testid={`approve-btn-${i}`}>
                    <CheckCircle className="h-4 w-4 mr-1" /> {processing[req.id] === 'approve' ? '...' : 'Approve'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-600/30 hover:bg-red-600/10"
                    disabled={!!processing[req.id]} onClick={() => handleAction(req.id, 'reject')} data-testid={`reject-btn-${i}`}>
                    <XCircle className="h-4 w-4 mr-1" /> {processing[req.id] === 'reject' ? '...' : 'Reject'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
